<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Relatório Analítico</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Informe o período, centro de custo e email para gerar o relatório analítico.
          </p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <form @submit.prevent="gerarRelatorio">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-5">
              <div class="flex flex-col gap-1">
                <label for="dataDe" class="text-sm font-medium text-gray-500">De</label>
                <input
                  v-model="form.dataDe"
                  type="date"
                  id="dataDe"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label for="dataAte" class="text-sm font-medium text-gray-500">Até</label>
                <input
                  v-model="form.dataAte"
                  type="date"
                  id="dataAte"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div v-if="$store.state.user.admin" class="flex flex-col gap-1">
                <label for="centrocusto" class="text-sm font-medium text-gray-500">
                  Centro de custo
                </label>

                <div class="relative group">
                  <select
                    v-model="form.centrocusto"
                    id="centrocusto"
                    class="h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 pr-10 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">Todos</option>
                    <option
                      v-for="centro in centroscusto"
                      :key="centro._id"
                      :value="{ nome: centro.nome, _id: centro._id }"
                    >
                      {{ centro.nome }}
                    </option>
                  </select>

                  <svg
                    class="select-icon h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label for="email" class="text-sm font-medium text-gray-500">Email</label>
                <input
                  v-model="form.email"
                  type="email"
                  id="email"
                  placeholder="email@exemplo.com"
                  class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div class="flex items-end md:col-span-5 lg:col-span-1">
                <button
                  type="submit"
                  class="h-11 w-full rounded-xl font-medium text-white transition"
                  :style="{ backgroundColor: primaryColor }"
                  @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                  @mouseleave="$event.target.style.backgroundColor = primaryColor"
                >
                  Gerar relatório
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="px-6 py-12 text-center">
          <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
            <svg class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 17v-2a4 4 0 014-4h5m0 0l-3-3m3 3l-3 3M5 19h4M5 5h14v4M5 9h8M5 13h4"
              />
            </svg>
          </div>

          <h2
            class="mt-5 text-2xl font-semibold text-gray-500"
            v-html="formatTitulo"
          ></h2>

          <p
            v-if="mensagemErro"
            class="mx-auto mt-3 max-w-2xl whitespace-pre-line rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          >
            {{ mensagemErro }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  data() {
    return {
      centroscusto: [],
      mensagemErro: "",
      tituloRelatorio: "Insira seu email e uma data de filtro para gerar o relatório",
      palavrasChave: ["email", "data de filtro", "gerado", "enviado", "faltou"],
      form: {
        dataDe: moment().startOf("week").format("YYYY-MM-DD"),
        dataAte: moment().endOf("week").format("YYYY-MM-DD"),
        centrocusto: "",
        email: ""
      }
    };
  },

  computed: {
    formatTitulo() {
      let tituloFormatado = this.tituloRelatorio;

      this.palavrasChave.forEach((palavra) => {
        tituloFormatado = tituloFormatado.replace(
          new RegExp(palavra, "gi"),
          `<span class="${palavra === "gerado" || palavra === "enviado" ? "text-green-700" : "text-gray-700"} font-bold">${palavra}</span>`
        );
      });

      return tituloFormatado;
    }
  },

  methods: {
    async gerarRelatorio() {
      this.mensagemErro = "";
      const erros = [];

      if (!this.form.email) {
        erros.push("O campo de email é obrigatório!");
      }

      if (!this.form.dataDe) {
        erros.push("O campo de data de início é obrigatório!");
      }

      if (!this.form.dataAte) {
        erros.push("O campo de data de término é obrigatório!");
      }

      if (this.form.dataDe && this.form.dataAte) {
        const dataDe = moment(this.form.dataDe);
        const dataAte = moment(this.form.dataAte);

        if (dataAte.isBefore(dataDe)) {
          erros.push("A data final não pode ser menor que a data inicial!");
        }

        const diferencaEmMeses = dataAte.diff(dataDe, "months", true);

        if (
          diferencaEmMeses >= 1 &&
          this.$store.state.user._id !== "683ee91ca7f9ff093228a755"
        ) {
          erros.push("A diferença entre as datas deve ser menor que 1 mês!");
        }
      }

      if (erros.length > 0) {
        this.tituloRelatorio = "Ops, parece que faltou algo!";
        this.mensagemErro = erros.join("\n");
        return;
      }

      const response = await this.$http.post("/v1/campanhasLZV2/gerar-analitico", {
        ...this.form
      });

      if (!response.data.success) {
        this.tituloRelatorio = "Ops, um erro ocorreu!";
        this.mensagemErro = response.data.err;
        return this.$vToastify.error(response.data.err);
      }

      this.tituloRelatorio = response.data.msg;
      return this.$vToastify.success(
        "Sucesso, o relatório será enviado para seu email dentro de alguns minutos!"
      );
    }
  },

  async beforeMount() {
    const centroscustoReq = await this.$http.post("/v1/centrocusto/list", {
      all: true
    });

    this.centroscusto = centroscustoReq.data.data || [];

    if (this.$store.state.user.centrocusto) {
      this.form.centrocusto = this.$store.state.user.centrocusto;
    }
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#centrocusto::-ms-expand {
  display: none;
}

#centrocusto {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>